

import { MessagePlugin } from 'tdesign-react';

import { AuditStatusType, BotAuditStatusDetailType } from '@/const/status';
import { IBot } from '@/interface';
import { Voice } from '@/interface/aigc';
import { postMaterial } from '@/utils/material';
import { transformObjectDeep } from '@/utils/tans/object';

import { apiPathServer } from './api-config';
import { CgiResp } from './cgi';
import { API_BOTH_PREFIX_TRPC, API_PREFIX, API_PREFIX_IPBOT, request } from './request';

/**
 * 保存bot信息
 */
export const modifyBotDetail = async (bot: Partial<IBot>) => {
  const { materialMap, ...postData }: any = bot;
  postData.materialMap = postMaterial(postData, materialMap);
  const res = await request<CgiResp<{ id: string }>>(`${API_PREFIX_IPBOT}${apiPathServer.postBot}`, {
    method: 'POST',
    body: transformObjectDeep(postData, 'snake', true),
  });
  return res;
};

/**
 * copy项目信息
 */
export const copyBot = async (id: string) => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.copyBot}`, {
    method: 'POST',
    body: { copy_id: id },
  });
  return res;
};

/**
 * 删除bot信息
 */
export const deleteBot = async (id: string) => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.deleteBot}`, {
    method: 'POST',
    body: { id },
  });
  return res;
};

interface IDeleteBotMemoryRequest {
  fromUserId: string;
  toUserId: string;
}

/**
 * 删除bot聊天记录
 */
export const deleteBotMemory = async (req: IDeleteBotMemoryRequest) => {
  const res = await request<CgiResp>(`${API_BOTH_PREFIX_TRPC}${apiPathServer.deleteBotMemory}`, {
    method: 'POST',
    body: transformObjectDeep({
      ...req,
      accessHead: JSON.stringify({ extra_request_head: { biz_id: 'nova' } }),
    }, 'camel'),
  });
  return res;
};

/**
 * 拉取bot详情
 */
export const fetchBotDetail = async ({ id = '', release = false }) => {
  const res = await request<CgiResp<IBot>>(`${API_PREFIX_IPBOT}${apiPathServer.botDetail}${release ? '/release' : ''}`, {
    query: { id },
  });
  return {
    ...res,
    data: transformObjectDeep(res.data, 'camel') as IBot,
  };
};

/**
 * bot提交审核
 */
export const submitBot = async (id: string) => {
  const res = await request(`${API_PREFIX}${apiPathServer.submitBot}`, {
    method: 'POST',
    body: { bot_id: id, next_status: BotAuditStatusDetailType.STATUS_DETAIL_TYPE_MANUAL_ING },
  });
  return res;
};


/**
 * bot审核意见
 */
export const getBotAuditMessage = async (botId: string) => {
  const res = await request<CgiResp<{ failed_reasons: string[] }>>(
    `${API_PREFIX}${apiPathServer.GetBotCheckOpinion}`,
    {
      query: transformObjectDeep({
        botId,
      }, 'snake'),
    },
  );
  return res.data.failed_reasons || [];
};


export enum ListCombinMode {
  MY_LIST = 0,
  PUBLIC_WITH_MY_LIST,
  PUBLIC_LIST,
}
/**
 *  拉取bot列表
 * @param param
 * @returns
 */
export const fetchBotList = async ({
  page = 1,
  size = 10,
  mode = ListCombinMode.MY_LIST,
  release = false,
  name,
}: {
  page?: number;
  size?: number;
  mode?: ListCombinMode;
  release?: boolean;
  name?: string;
}): Promise<{
  list: IBot[];
  pageInfo: {
    total: number;
    current: number;
    pageSize: number;
  }
}> => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.botList}${release ? '/release' : ''}`, {
    query: {
      page,
      size,
      mode,
      name,
    },
  });
  const { list = [], pageinfo: pageInfo } = res?.data || {};
  return {
    list: list?.map((item: any) => transformObjectDeep(item, 'camel')) || [],
    pageInfo: {
      total: pageInfo?.total || 0,
      current: pageInfo?.page || 1,
      pageSize: pageInfo?.size || 10,
    },
  };
};

/**
 * 本幕角色 Bot选项
 * @param param
 * @returns
 */
export const getBotOptions = async ({
  page = 1,
  size = 10,
}: {
  page?: number;
  size?: number;
}): Promise<{
  list: IBot[];
  pageInfo: {
    total: number;
    current: number;
    pageSize: number;
  }
}> => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.botOptions}`, {
    query: { page, size },
  });
  const { list = [], pageinfo: pageInfo } = res?.data || {};
  return {
    list: list?.map((item: any) => transformObjectDeep(item, 'camel')) || [],
    pageInfo: {
      total: pageInfo?.total || 0,
      current: pageInfo?.page || 1,
      pageSize: pageInfo?.size || 10,
    },
  };
};

export enum SortRule {
  DESC = 0,
  ASC
}

export enum BotSortType {
  BOTLISTSORTTYPE_SHOWINDEX_DEFAULT = 0,
  BOTLISTSORTTYPE_PUBLISH_TIME = 3,
  BOTLISTSORTTYPE_UPDATE_TIME = 4
}

export const fetchBotFromStoryList = async ({
  page = 1,
  size = 20,
  storyId = '',
  sortRule = SortRule.DESC,
  sortType = BotSortType.BOTLISTSORTTYPE_PUBLISH_TIME,
  status = [],
  name,
  release = false,
}: {
  page?: number;
  size?: number;
  name?: string;
  storyId?: string;
  sortRule?: SortRule;
  sortType?: BotSortType;
  status?: AuditStatusType[];
  release?: boolean;
}): Promise<{
  list: IBot[];
  pageInfo: {
    total: number;
    current: number;
    pageSize: number;
  }
}> => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.botListByStory}${release ? '/release' : ''}`, {
    query: transformObjectDeep({
      page,
      size,
      storyId,
      name,
      sortConfig: JSON.stringify(transformObjectDeep({
        sortType,
        sortRule,
      }, 'snake')),
      status: JSON.stringify(status.map(item => Number(item))),
    }, 'snake'),
  });
  const { list = [], pageinfo: pageInfo } = res?.data || {};
  return {
    list: list?.map((item: any) => transformObjectDeep(item, 'camel')) || [],
    pageInfo: {
      total: pageInfo?.total || 0,
      current: pageInfo?.page || 1,
      pageSize: pageInfo?.size || 20,
    },
  };
};

/**
 *  拉取对应space下的bot列表
 * @param param0
 * @returns
 */
export const fetchBotListBySpace = async ({
  page = 1,
  size = 10,
  spaceId = '',
  release = false,
}: {
  page?: number;
  size?: number;
  spaceId: string;
  release?: boolean;
}): Promise<{
  list: IBot[];
  pageInfo: {
    total: number;
    current: number;
    pageSize: number;
  }
}> => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.botListBySpace}${release ? '/release' : ''}`, {
    query: {
      page,
      size,
      space_id: spaceId,
    },
  });
  const { list = [], pageinfo: pageInfo } = res?.data || {};
  return {
    list: list?.map((item: any) => transformObjectDeep(item, 'camel')) || [],
    pageInfo: {
      total: pageInfo?.total || 0,
      current: pageInfo?.page || 1,
      pageSize: pageInfo?.size || 10,
    },
  };
};

/**
 * 拉取知识库列表
 */
export const fetchKnowledgeList = async (): Promise<{ id: string; name: string }[]> => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.knowledgeList}`);
  return res.data?.map((item: { id: string; name: string }) => transformObjectDeep(item, 'camel')) || [];
};

/**
 * 上传知识库
 */
export const postKnowledge = async (req: { knowledgeName: string; contentUrl: string }) => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.postKnowledge}`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (res.code && res.code !== 0 && 'msg' in res) {
    throw new Error(res.msg as string);
  }
};

/**
 * 拉取语音包列表
 */
export const fetchVoiceList = async (): Promise<Voice[]> => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.voiceList}`);
  return res.data?.map((item: Voice) => transformObjectDeep(item, 'camel')) || [];
};

/**
 * 拉取模板列表
 */
export const fetchTemplateList = async (): Promise<{ id: number; promptName: string; versionName: string }[]> => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.templateList}`);
  return res.data?.map((item: { id: number; promptName: string; versionName: string }) => transformObjectDeep(item, 'camel')) || [];
};


/**
 * 拉取技能列表
 */
export const fetchSkillList = async (): Promise<{ id: string; name: string }[]> => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.skillList}`);
  return res.data?.map((item: { id: string; name: string }) => transformObjectDeep(item, 'camel')) || [];
};


/**
 * 拉取类别列表
 */
export const fetchTagList = async (): Promise<{ name: string }[]> => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.tagList}`);
  return (res.data || []).map((item: { name: string }) => transformObjectDeep(item, 'camel'));
};

/**
 * 拉取CAI模型列表
 */
export const fetchCAIModelList = async (): Promise<{ id: string; name: string }[]> => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.CAImodelList}`);
  return (res.data || []).map((item: { id: string; name: string }) => transformObjectDeep(item, 'camel'));
};

/**
 * 拉取VENUS模型列表
 */
export const fetchVENUSModelList = async (): Promise<{ id: string; name: string }[]> => {
  const res = await request(`${API_PREFIX_IPBOT}${apiPathServer.VENUSmodelList}`);
  return res.data
    .map((item: { model_id: string; model_name: string }) => ({ id: item.model_id, name: item.model_name }));
};

/**
 * 智能创作
 * @param id
 * @returns
 */
export const generateBot = async (desc: string) => {
  const res = await request<CgiResp<string>>(`${API_PREFIX_IPBOT}${apiPathServer.generateBot}`, {
    method: 'POST',
    body: transformObjectDeep({ description: desc }, 'snake'),
  });
  if (res.code !== 0) {
    MessagePlugin.error(res.message || res.msg || `${apiPathServer.generateBot} failed: 后台服务错误`);
    return;
  }
  return res.data || '';
};

interface IChatPrivateRequest {
  userId: string;
  botsAt: [string];
  chatMessage: string;
}

export const chatPrivate = async (req: IChatPrivateRequest) => {
  const res = await request<CgiResp<string>>(`${API_BOTH_PREFIX_TRPC}${apiPathServer.ChatSingle}`, {
    method: 'POST',
    body: transformObjectDeep(req, 'snake'),
  });
  if (res.code !== 0) {
    MessagePlugin.error(res.message || res.msg || `${apiPathServer.generateBot} failed: 后台服务错误`);
    return;
  }
};
